import { useState, useEffect } from "react";
import './EditHRDocumentRegistry.css';
import axiosInstance from '../../../Config/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDropzone } from 'react-dropzone';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
// Importurile pentru DatePicker
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Înregistrăm localizarea pentru react-datepicker
registerLocale("ro", ro);

export default function EditHRDocumentRegistry() {
  // Stări pentru datele formularului, fișiere, erori și mesaje de confirmare
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    code: '',
    direction: '',
    date: '',
    sender: '',
    recipient: '',
    content: '',
  });
  // Stare pentru DatePicker
  const [selectedDate, setSelectedDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [existingFile, setExistingFile] = useState('');
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());
  const [fileAddedMessage, setFileAddedMessage] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [errorNotification, setErrorNotification] = useState('');
  const openPdfPreview = async () => {
    try {
      const fileName = existingFile.split('/').pop();
      const extension = fileName.split('.').pop().toLowerCase();

      if (extension !== 'pdf') {
        showErrorNotification('Preview available only for PDF.');
        return;
      }

      const response = await axiosInstance.get(existingFile, {
        responseType: 'blob'
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);

      const newWindow = window.open();
      newWindow.document.write(`
      <html>
        <head>
          <title>${fileName}</title>
        </head>
        <body style="margin:0">
          <embed src="${fileURL}" type="application/pdf" width="100%" height="100%" />
        </body>
      </html>
    `);
      newWindow.document.close();
    } catch (error) {
      showErrorNotification('Unable to preview file.');
      console.error(error);
    }
  };


  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'image/jpeg': [],
      'image/png': []
    },
    multiple: false,
    maxSize: 1024 * 1024 * 1024, // 1GB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
        setRemoveExistingFile(true);

        setFileAddedMessage(`File "${acceptedFiles[0].name}" added successfully!`);

        const file = acceptedFiles[0];
        if (file.type === 'application/pdf') {
          const url = URL.createObjectURL(file);
          setPdfPreviewUrl(url);
        } else {
          setPdfPreviewUrl(null);
        }

        setTimeout(() => {
          setFileAddedMessage('');
        }, 3000);
      }
    }
  });

  const showErrorNotification = (message) => {
    setErrorNotification(message);
    setTimeout(() => {
      setErrorNotification('');
    }, 3000);
  };

  // La montare, încarcă datele documentului din API
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axiosInstance.get(`/hr-document-registry/${id}/`);
        const data = response.data;

        const normalizedDirection = data.direction?.toLowerCase() === 'input'
          ? 'Input'
          : data.direction?.toLowerCase() === 'output'
            ? 'Output'
            : '';

        setFormData({
          code: data.code || '',
          direction: normalizedDirection,
          date: data.date || '',
          sender: data.sender || '',
          recipient: data.recipient || '',
          content: data.content || '',
        });

        // Setăm data pentru DatePicker
        if (data.date) {
          setSelectedDate(new Date(data.date));
        }

        setExistingFile(data.attachments || '');
      } catch (error) {
        console.error("Error fetching document:", error);
        alert("Failed to load document data.");
      }
    };

    if (id) fetchDocument();
  }, [id]);

  // Funcție pentru gestionarea datelor din DatePicker
  const handleDateChange = (date) => {
    // Convertim data în format YYYY-MM-DD pentru backend
    const isoDate = date ? date.toISOString().split("T")[0] : "";
    
    setFormData(prev => ({
      ...prev,
      date: isoDate
    }));

    // Actualizăm și state-ul pentru DatePicker
    setSelectedDate(date);
  };

  // Actualizează statusurile la modificarea inputurilor text (excluzând data)
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name !== 'date') { // Excludem câmpul date deoarece este gestionat de DatePicker
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(prevFiles => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (updatedFiles.length === 0) {
        setDropzoneKey(Date.now());
      }
      return updatedFiles;
    });
  };

  // Actualizează direcția 
  const handleRadioChange = (e) => {
    setFormData(prev => ({ ...prev, direction: e.target.value }));
  };

  // Returnează iconița potrivită în funcție de tipul fișierului
  const getPreviewIcon = (fileObject) => {
    const { file } = fileObject;
    const fileType = file.type;

    let icon;
    if (fileType === 'application/pdf') {
      icon = <PictureAsPdfIcon style={{ fontSize: 40, color: '#d32f2f' }} />;
    } else if (
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      icon = <DescriptionIcon style={{ fontSize: 40, color: '#1976d2' }} />;
    } else {
      icon = <InsertDriveFileIcon style={{ fontSize: 40, color: '#757575' }} />;
    }
    return (
      <div title={file.name}>
        {icon}
      </div>
    );
  };


  // Tratează trimiterea formularului
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    let newErrors = {};
    if (!formData.sender) newErrors.sender = "Sender is required";
    if (!formData.recipient) newErrors.recipient = "Recipient is required";
    if (!formData.content) newErrors.content = "Content is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.direction) newErrors.direction = "Direction is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      const submissionData = new FormData();
      submissionData.append('code', formData.code);
      submissionData.append('direction', formData.direction.toLowerCase());
      submissionData.append('date', formData.date);
      submissionData.append('sender', formData.sender);
      submissionData.append('recipient', formData.recipient);
      submissionData.append('content', formData.content);

      if (removeExistingFile) {
        submissionData.append('remove_file', 'true');
      }

      if (files.length > 0) {
        submissionData.append('attachments', files[0]);
      }

      const response = await axiosInstance.put(`/hr-document-registry/${id}/`, submissionData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("Updated:", response.data);
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate('/hr-document-registry', { replace: true });
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Error editing:", error.response?.data || error.message);
      alert("Error editing document");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="edit-hr-document-registry_page">

      {errorNotification && (
        <div className="error-notification">
          {errorNotification}
        </div>
      )}

      {fileAddedMessage && (
        <div className="success-snackbar">{fileAddedMessage}</div>
      )}

      {/* Mesaj succes după salvare */}
      {showSuccessMessage && (
        <div className="success-popup">
          Document edited successfully!
        </div>
      )}

      <div className="registration-header">
        {/* Header cu titlu și buton de întoarcere */}
        <ArrowBackIcon className="back-icon" onClick={() => navigate(-1)} />
        <h2>Registration change {formData.code}</h2>
      </div>
      <hr className="header-divider" />

      {/* Formularul propriu-zis pentru editare document */}
      <div className="registration-card">
        <form onSubmit={handleSubmit} className="edit-document-form">
          <div className="form-group">
            <label>Code</label>
            <input type="text" name="code" value={formData.code} disabled />
          </div>

          <div className="form-group">
            <label>Direction</label>
            <div className="radio-group">
              <label>
                <input type="radio" value="Output" checked={formData.direction === "Output"} onChange={handleRadioChange} />
                Output
              </label>
              <label>
                <input type="radio" value="Input" checked={formData.direction === "Input"} onChange={handleRadioChange} />
                Input
              </label>
            </div>
            {errors.direction && <div className="error-message">{errors.direction}</div>}
          </div>

          {/* MODIFICARE: Înlocuim input type="date" cu DatePicker */}
          <div className="form-group">
            <label>Registration date <span className="required">*</span></label>
            <div className="datepicker-container">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                dateFormat="dd.MM.yyyy"
                locale="ro"
                placeholderText="dd.mm.yyyy"
                className="datepicker-input"
                required
                wrapperClassName="datepicker-wrapper"
              />
              <span className="calendar-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
            </div>
            {errors.date && <div className="error-message">{errors.date}</div>}
          </div>

          <div className="form-group">
            <label>Sender name <span className="required">*</span></label>
            <input type="text" name="sender" value={formData.sender} onChange={handleChange} />
            {errors.sender && <div className="error-message">{errors.sender}</div>}
          </div>

          <div className="form-group">
            <label>Recipient name <span className="required">*</span></label>
            <input type="text" name="recipient" value={formData.recipient} onChange={handleChange} />
            {errors.recipient && <div className="error-message">{errors.recipient}</div>}
          </div>

          <div className="form-group">
            <label>Document content <span className="required">*</span></label>
            <input type="text" name="content" value={formData.content} onChange={handleChange} />
            {errors.content && <div className="error-message">{errors.content}</div>}
          </div>

          <div className="form-group">
            <label>Upload document</label>
            <div key={dropzoneKey}>
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  {isDragActive ? (
                    <p>Drop the file here...</p>
                  ) : (
                    <p>Drag & drop or click to select a file</p>
                  )}
                  <CloudUploadIcon style={{ fontSize: 40, color: '#888' }} />
                </div>

                {files.length > 0 && (
                  <div className="preview-container">
                    {files.map((file, index) => {
                      const isPdf = file.type === 'application/pdf';

                      return (
                        <div
                          key={index}
                          className="file-preview"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPdf && pdfPreviewUrl) {
                              const newWindow = window.open();
                              newWindow.document.write(`
                              <html>
                                <head>
                                  <title>${file.name}</title>
                                </head>
                                <body style="margin:0">
                                  <embed src="${pdfPreviewUrl}" type="application/pdf" width="100%" height="100%" />
                                </body>
                              </html>
                            `);
                              newWindow.document.close();
                            } else {
                              showErrorNotification('Preview is only available for PDF files.');
                            }
                          }}

                          style={{ cursor: 'pointer' }}
                          title={isPdf ? 'Click to preview PDF' : 'Preview available only for PDF'}
                        >
                          {getPreviewIcon({ file })}
                          <span className="file-name">{file.name}</span>
                          <button
                            type="button"
                            className="trash-file-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            title="Remove file"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="file-hint">
                * Accepted file types: PDF, Word (.doc/.docx), JPG, PNG. Only one file allowed.
              </p>
            </div>

            {existingFile && !removeExistingFile && (() => {
              const fileName = existingFile.split('/').pop();
              const extension = fileName.split('.').pop().toLowerCase();
              const isPdf = extension === 'pdf';

              return (
                <div className="existing-file-preview">
                  <p>
                    Current file:&nbsp;
                    <span
                      className="file-link"
                      style={{
                        textDecoration: isPdf ? 'underline' : 'none',
                        color: isPdf ? 'blue' : 'black',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => {
                        if (isPdf) {
                          openPdfPreview();
                        } else {
                          showErrorNotification('Preview available only for PDF.');
                        }
                      }}
                      title={isPdf ? "Click to preview" : "Preview available only for PDF"}
                    >
                      {fileName}
                    </span>
                    &nbsp;
                    <button
                      type="button"
                      className="remove-file-button"
                      onClick={() => setRemoveExistingFile(true)}
                    >
                      Remove
                    </button>
                  </p>
                </div>
              );
            })()}


          </div>

          <div className="form-buttons">
            <button type="button" className="discard" onClick={() => navigate(-1)}>
              Discard
            </button>
            <button
              type="submit"
              className="save"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}